import { db } from './index.js';
import { LIBRARY_CHANGED_EVENT, TAB_EDITS_CHANGED_EVENT } from '../sync/events.js';
import { toDifficultyLevel } from '../utils/difficulty.js';
import type {
  TabSong,
  LocalTabEdit,
  LocalTabEditStatus,
  TabEditKind,
  ScoreDraft
} from '../types/index.js';

export function notifyTabEditsChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TAB_EDITS_CHANGED_EVENT));
  }
}

export interface ApplyServerTabStateOptions {
  skipKinds?: TabEditKind[];
  force?: boolean;
}

/** Inserts a new edit into the local queue. */
export async function queueTabEdit(
  edit: Omit<LocalTabEdit, 'attempts' | 'createdAt' | 'updatedAt'>
): Promise<LocalTabEdit> {
  const now = new Date().toISOString();
  const record: LocalTabEdit = {
    ...edit,
    status: edit.status ?? 'QUEUED',
    attempts: 0,
    createdAt: now,
    updatedAt: now
  };
  await db.tabEdits.put(record);
  notifyTabEditsChanged();
  return record;
}

/** Retrieves queued edits for a user, sorted oldest first. */
export async function getQueuedEdits(uid: string): Promise<LocalTabEdit[]> {
  const edits = await db.tabEdits
    .where('[ownerId+status]')
    .equals([uid, 'QUEUED'])
    .toArray();
  return edits.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Updates an edit's status and server metadata after sync. */
export async function markEditSent(
  id: string,
  serverEdit: {
    status: LocalTabEditStatus;
    appliedRevision?: number | null;
    reviewedAt?: string | null;
  }
): Promise<void> {
  const now = new Date().toISOString();
  await db.tabEdits.update(id, {
    status: serverEdit.status,
    appliedRevision: serverEdit.appliedRevision ?? null,
    reviewedAt: serverEdit.reviewedAt ?? null,
    updatedAt: now
  });
}

/** Deletes an edit from the local database. */
export async function dropEdit(id: string): Promise<void> {
  await db.tabEdits.delete(id);
  notifyTabEditsChanged();
}

/** Marks a pending edit for withdrawal on next sync. */
export async function markWithdrawPending(id: string): Promise<void> {
  const now = new Date().toISOString();
  await db.tabEdits.update(id, {
    pendingWithdraw: 1,
    updatedAt: now
  });
  notifyTabEditsChanged();
}

/**
 * Upserts edits pulled from GET /api/tabs/mine/edits.
 * Preserves local QUEUED rows and rows marked for withdrawal.
 */
export async function upsertServerEdits(edits: any[], ownerId: string): Promise<void> {
  if (!edits || edits.length === 0) return;
  await db.transaction('rw', db.tabEdits, async () => {
    for (const edit of edits) {
      try {
        const existing = await db.tabEdits.get(edit.id);
        if (existing && (existing.status === 'QUEUED' || existing.pendingWithdraw === 1)) {
          continue;
        }

        const isDecided = edit.status === 'APPROVED' || edit.status === 'REJECTED';
        let decisionSeen = existing?.decisionSeen;
        if (isDecided) {
          if (!existing) {
            decisionSeen = 0;
          } else if (existing.status !== edit.status) {
            decisionSeen = 0;
          }
        }

        const row: LocalTabEdit = {
          id: edit.id,
          tabId: edit.tabId,
          ownerId,
          kind: edit.kind,
          payload: edit.payload,
          baseRevision: edit.baseRevision,
          note: edit.note ?? null,
          status: edit.status,
          mode: existing?.mode ?? 'suggest',
          origin: existing?.origin,
          createdAt: edit.createdAt,
          updatedAt: edit.updatedAt ?? new Date().toISOString(),
          attempts: existing?.attempts ?? 0,
          rejectReason: edit.rejectReason ?? null,
          reviewedAt: edit.reviewedAt ?? null,
          appliedRevision: edit.appliedRevision ?? null,
          tabTitle: edit.tab?.title ?? existing?.tabTitle,
          decisionSeen
        };

        await db.tabEdits.put(row);
      } catch (err) {
        console.error('upsertServerEdits: failed to upsert edit', edit?.id, err);
      }
    }
  });
  notifyTabEditsChanged();
}

/** Marks all decision edits for a user as seen (decisionSeen = 1). */
export async function markDecisionsSeen(ownerId: string): Promise<void> {
  await db.tabEdits
    .where('ownerId')
    .equals(ownerId)
    .filter((e) => e.decisionSeen === 0)
    .modify({ decisionSeen: 1 });
  notifyTabEditsChanged();
}

/** Returns true if there is any edit where ownerId === uid && decisionSeen === 0. */
export async function hasUnseenDecisions(ownerId: string): Promise<boolean> {
  const item = await db.tabEdits
    .where('ownerId')
    .equals(ownerId)
    .filter((e) => e.decisionSeen === 0)
    .first();
  return Boolean(item);
}

/** Retrieves all non-withdrawn edits for a user, newest first. */
export async function getMyEditsList(ownerId: string): Promise<LocalTabEdit[]> {
  const all = await db.tabEdits.where('ownerId').equals(ownerId).toArray();
  return all
    .filter((e) => e.status !== 'WITHDRAWN' && e.pendingWithdraw !== 1)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Retrieves all local edits for a tab belonging to a user, newest first. */
export async function getMyEditsForTab(tabId: string, ownerId: string): Promise<LocalTabEdit[]> {
  const all = await db.tabEdits.where('tabId').equals(tabId).toArray();
  return all
    .filter((e) => e.ownerId === ownerId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/**
 * Optimistically applies a direct edit to the local tab row.
 */
export async function applyLocalDirectEdit(
  tabId: string,
  kind: TabEditKind,
  payload: any
): Promise<void> {
  await db.transaction('rw', db.tabs, async () => {
    const cur = await db.tabs.get(tabId);
    if (!cur) return;
    const now = new Date().toISOString();
    const patch: Partial<TabSong> = { updatedAt: now };

    if (kind === 'DETAILS') {
      if (typeof payload?.title === 'string') patch.title = payload.title.trim();
      if (typeof payload?.artist === 'string') patch.artist = payload.artist.trim();
      if (typeof payload?.tuning === 'string') patch.tuning = payload.tuning.trim();
    } else if (kind === 'DIFFICULTY') {
      const rhythm = payload?.rhythm_difficulty !== undefined ? payload.rhythm_difficulty : payload?.rhythmDifficulty;
      if (rhythm !== undefined) patch.rhythmDifficulty = toDifficultyLevel(rhythm);
      const lead = payload?.lead_difficulty !== undefined ? payload.lead_difficulty : payload?.leadDifficulty;
      if (lead !== undefined) patch.leadDifficulty = toDifficultyLevel(lead);
    } else if (kind === 'LYRICS') {
      if (typeof payload?.content === 'string') {
        patch.lyricsContent = payload.content || null;
        patch.lyricsUpdatedAt = now;
      }
    } else if (kind === 'SCORE') {
      if (Array.isArray(payload?.ops)) {
        patch.scoreEdits = [
          ...(cur.scoreEdits || []),
          {
            id: `local-${Date.now()}`,
            appliedRevision: (cur.revision ?? 1) + 1,
            ops: payload.ops
          }
        ];
      }
    }

    await db.tabs.update(tabId, patch);
  });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
  }
}

/**
 * Updates a tab with server state while protecting local edits that are still QUEUED.
 */
export async function applyServerTabState(
  id: string,
  state: Partial<TabSong>,
  opts: ApplyServerTabStateOptions = {}
): Promise<boolean> {
  const changed = await db.transaction('rw', [db.tabs, db.tabEdits], async () => {
    const cur = await db.tabs.get(id);
    if (!cur) return false;

    const skipKindsSet = new Set<TabEditKind>(opts.skipKinds ?? []);
    if (!opts.force) {
      if (cur.pendingDifficulty === 1) {
        skipKindsSet.add('DIFFICULTY');
      }
      if (!opts.skipKinds) {
        const queuedDirect = await db.tabEdits
          .where('tabId')
          .equals(id)
          .filter((e) => e.status === 'QUEUED' && e.mode === 'direct')
          .toArray();
        for (const e of queuedDirect) {
          skipKindsSet.add(e.kind);
        }
      }
    }

    const patch: Partial<TabSong> = {};

    if (!skipKindsSet.has('DETAILS')) {
      if (state.title !== undefined && state.title !== cur.title) patch.title = state.title;
      if (state.artist !== undefined && state.artist !== cur.artist) patch.artist = state.artist;
      if (state.tuning !== undefined && state.tuning !== cur.tuning) patch.tuning = state.tuning;
    }

    if (!skipKindsSet.has('DIFFICULTY')) {
      const rhythm = state.rhythmDifficulty !== undefined ? state.rhythmDifficulty : (state as any).rhythm_difficulty;
      if (rhythm !== undefined) {
        const v = toDifficultyLevel(rhythm);
        if ((cur.rhythmDifficulty ?? null) !== v) patch.rhythmDifficulty = v;
      }
      const lead = state.leadDifficulty !== undefined ? state.leadDifficulty : (state as any).lead_difficulty;
      if (lead !== undefined) {
        const v = toDifficultyLevel(lead);
        if ((cur.leadDifficulty ?? null) !== v) patch.leadDifficulty = v;
      }
    }

    if (!skipKindsSet.has('LYRICS')) {
      if (state.lyricsContent !== undefined && state.lyricsContent !== cur.lyricsContent) {
        patch.lyricsContent = state.lyricsContent;
        if (state.lyricsUpdatedAt !== undefined) patch.lyricsUpdatedAt = state.lyricsUpdatedAt;
      }
    }

    if (!skipKindsSet.has('SCORE')) {
      if (state.scoreEdits !== undefined) {
        patch.scoreEdits = state.scoreEdits;
      }
    }

    if (state.revision !== undefined && state.revision !== cur.revision) patch.revision = state.revision;
    if (state.status !== undefined && state.status !== cur.status) patch.status = state.status;
    if (state.updatedAt !== undefined && state.updatedAt !== cur.updatedAt) patch.updatedAt = state.updatedAt;
    if (state.hasLyrics !== undefined && state.hasLyrics !== cur.hasLyrics) patch.hasLyrics = state.hasLyrics;
    if (state.extrasRevision !== undefined && state.extrasRevision !== cur.extrasRevision) patch.extrasRevision = state.extrasRevision;

    if (Object.keys(patch).length === 0) return false;

    await db.tabs.update(id, patch);
    return true;
  });

  if (changed && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
  }

  return changed;
}

/**
 * Stores lyrics and score edits retrieved from GET /api/tabs/:id/extras on the local tab row.
 */
export async function writeTabExtras(id: string, extras: any): Promise<void> {
  if (!extras) return;
  const changed = await db.transaction('rw', db.tabs, async () => {
    const cur = await db.tabs.get(id);
    if (!cur) return false;
    const patch: Partial<TabSong> = {
      extrasRevision: extras.revision ?? cur.revision
    };
    if (extras.lyrics !== undefined) {
      patch.lyricsContent = extras.lyrics ? extras.lyrics.content : null;
      patch.lyricsUpdatedAt = extras.lyrics?.updatedAt ?? (extras.lyrics ? new Date().toISOString() : undefined);
    }
    if (extras.scoreEdits !== undefined) {
      patch.scoreEdits = extras.scoreEdits;
    }
    if (extras.revision !== undefined) {
      patch.revision = extras.revision;
    }
    await db.tabs.update(id, patch);
    return true;
  });
  if (changed && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
  }
}

/** Retrieves a local score draft. */
export async function getScoreDraft(ownerId: string, tabId: string): Promise<ScoreDraft | undefined> {
  return db.scoreDrafts.get([ownerId, tabId]);
}

/** Saves or updates a score draft. */
export async function putScoreDraft(draft: ScoreDraft): Promise<void> {
  const now = new Date().toISOString();
  await db.scoreDrafts.put({
    ...draft,
    updatedAt: draft.updatedAt || now
  });
}

/** Deletes a score draft. */
export async function deleteScoreDraft(ownerId: string, tabId: string): Promise<void> {
  await db.scoreDrafts.delete([ownerId, tabId]);
}
